import { useEffect, useRef, useState } from 'react';
import { emergencyExit } from '@/api/endpoints';
import type { ControlRequestRow, Position } from '@/api/types';
import { waitForControlRequest } from '@/lib/controlRequests';
import { describe } from '@/lib/errors';
import { formatMoney, formatQuantity } from '@/lib/format';

/**
 * Confirmation for selling a whole position now, no lower than 2% under the best bid.
 *
 * Three layers against a click by error: this dialog says exactly what will
 * happen, the symbol has to be typed back, and the server then asks for the
 * password again (step-up) before anything is queued. Only a SUPER_ADMIN sees
 * the button that opens it.
 */
export function EmergencyExitDialog({
  accountId,
  accountLabel,
  mode,
  position,
  onClose,
}: {
  accountId: number;
  accountLabel: string;
  mode: string;
  position: Position;
  onClose: (result: ControlRequestRow | null) => void;
}) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const field = useRef<HTMLInputElement>(null);

  useEffect(() => field.current?.focus(), []);

  const matches = typed.trim().toUpperCase() === position.symbol;
  const ready = matches && reason.trim().length >= 5 && !busy;

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const queued = await emergencyExit({
        account_id: accountId,
        symbol: position.symbol,
        confirm: typed.trim().toUpperCase(),
        reason: reason.trim(),
      });
      setStatus('Queued. Cancelling open orders and selling…');
      const finished = await waitForControlRequest(queued.id);
      onClose(finished);
    } catch (err) {
      setError(describe(err));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" role="dialog" aria-modal="true" aria-labelledby="exit-title">
      <div className="panel dialog">
        <div className="panel-head">
          <h2 id="exit-title">Emergency exit: {position.symbol}</h2>
        </div>
        <div className="panel-body" style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <div className="notice notice--error">
            This sells the whole position on {accountLabel} ({mode.toLowerCase()}), now, at
            the best prices the order book offers down to 2% below the best bid. Anything the
            book cannot take within that is kept and reported. It cancels this account&apos;s
            open orders in {position.symbol} first, and it sells even at a loss and even while
            the symbol is blocked. It cannot be undone.
          </div>
          <dl className="account__totals">
            <div>
              <dt>Quantity</dt>
              <dd className="num">{formatQuantity(position.qty, 8)}</dd>
            </div>
            <div>
              <dt>Value now</dt>
              <dd className="num">{formatMoney(position.market_value)}</dd>
            </div>
            <div>
              <dt>Unrealised</dt>
              <dd className={`num ${position.unrealised.startsWith('-') ? 'loss' : 'gain'}`}>
                {formatMoney(position.unrealised)}
              </dd>
            </div>
          </dl>
          {error ? <div className="field-error">{error}</div> : null}
          {status ? <div className="notice">{status}</div> : null}
          <div>
            <label htmlFor="exit-confirm">
              Type <strong>{position.symbol}</strong> to confirm
            </label>
            <input
              id="exit-confirm"
              ref={field}
              value={typed}
              onChange={(event) => setTyped(event.target.value)}
              autoComplete="off"
            />
          </div>
          <div>
            <label htmlFor="exit-reason">Reason (recorded in the audit trail)</label>
            <input
              id="exit-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              placeholder="at least 5 characters"
            />
          </div>
          <div className="row-actions">
            <button type="button" onClick={() => onClose(null)} disabled={busy}>
              Keep the position
            </button>
            <button type="button" className="danger" disabled={!ready} onClick={() => void confirm()}>
              {busy ? 'Exiting…' : 'Sell it all now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
